import type { CardPhotoSlot } from '../photo-slots'
import { Fragment, useEffect, useRef, useState } from 'react'
import * as LucideIcons from 'lucide-react'
import useSEO from '../hooks/useSEO'
import { shouldSendOnEnter } from '../utils/inputKeys'
import { useGenerate } from '../context/GenerateContext'
import { Coin } from '../icons/Coin'
import { useLocation, useNavigate } from 'react-router-dom'
import { useOpenAuth } from '../hooks/useAuthModal'
import { brandModelToModel, useProviders } from '../hooks/useProviders'
import type { Model, ModelCategory, PopularModelCard as PopularModelCardType } from '../data/models'
import type { CardKind, GenerationCardSettings } from '../types/generationCard'
import { attachFromPopularCard, attachFromVideoItem } from '../api/attachedStyleCard'
import { detectBrand, resolveModelIdAliases } from '../data/brands'
import ModelIcon from '../components/ModelIcon'
import { pluralizeTokens } from '../components/TokenPrice'
import { PopularModelCard } from '../components/PopularModelCard'
import DragScrollRow from '../components/DragScrollRow'
import { cardInputVideoContext, computeModelCost } from '../utils/modelCost'
import { effectiveCardCover, trendMemberPreview, trendMembersOf, type BackendCard } from './bots/utils/popularCardApply'
import { cardPrimaryUrl } from './bots/utils/cardSeo'
import { activeDiscount, gatePromoByCost, trendDiscount, trendTotalCost, type TrendPipePricing } from '../utils/discount'
// Bg_moon_* decorative shapes удалены — заменены на brand-reactive
// home-ambient-aura (см. .home-ambient-aura в _chat.scss). Они были
// статичными декорациями 2020-х, не сочетались с новой brand-themed
// атмосферой (aura меняется по выбранной модели). Aura + чёрный canvas
// = чище, modern minimalist look (Linear / Vercel / Stripe style).
import { uploadChatFile } from '../api/chat'
import { useAuth } from '../context/AuthContext'
import api from '../api/client'
import FaqAccordion from '../components/FaqAccordion'
import { useSelectedModel } from '../context/SelectedModelContext'
import styles from '../styles/components/_popular-models.module.scss'
import PopularVideoShowcase, { type PopularVideoItem } from '../components/PopularVideoShowcase.tsx'
import FeedSkeleton from '../components/FeedSkeleton'
import TrendFeedSection, { applyFeedCardOrder } from '../components/TrendFeedSection.tsx'
import { getModelConfig } from './generate/utils/generateModelConfig'
import { useWelcomeIntent, type IntentType, type WelcomeIntent } from '../hooks/useWelcomeIntent'
import { saveLastModel } from './bots/utils/widgetResolver'
import SettingsPillsRow from './generate/components/SettingsPillsRow'
import UploadsRow from './generate/components/UploadsRow'
import HomeTabs from './home/HomeTabs'
import HomeFeedBlock from './home/HomeFeedBlock'
import ShowcaseBlock from './home/ShowcaseBlock'
import { isFrameSection, type HomeSection } from './home/homeBlocks'
import { isStaff } from '../utils/roles'

const API_BASE = import.meta.env.VITE_API_URL || ''

// Placeholder для textarea меняется по типу выбранного intent — нужно
// чтобы юзер понимал ЧТО писать (изображение vs видео vs песня vs
// презентация). Без adaptive-placeholder'а универсальный «Что хочешь
// узнать или обсудить?» сбивает с толку когда выбрана image-модель —
// «обсудить» там не по теме.
//
// Priority resolution (см. chat_input case ниже):
//   1. model.paramHints.prompt.description (admin может override per-model)
//   2. INTENT_PLACEHOLDERS[selectedIntent.type] (по категории)
//   3. cfg.placeholder из page-section (legacy fallback)
//   4. Дефолт «Что хочешь узнать или обсудить?»
const INTENT_PLACEHOLDERS: Record<IntentType, string> = {
	text: 'Задайте вопрос ИИ — например: объясни квантовую запутанность простыми словами…',
	image: 'Опишите изображение: «Космический пейзаж с двумя лунами, кинематографичный стиль…»',
	video: 'Опишите видео-сцену: «Котёнок бегает по пляжу на закате, медленная камера…»',
	audio: 'Опишите песню или идею: «Энергичный поп-трек про лето, женский вокал…»',
	presentation: 'Тема презентации: «История ИИ» / «Бизнес-план кофейни» / «Основы React»…',
}

// Резолв route для секции popular_category — определяется по cfg.title /
// cfg.category. Используется ниже в `case 'popular_category'` для линка
// «Все стили ›». Если ни одно правило не сматчилось, линк не рендерится.
function getCategoryRoute(cfg: { title?: string; category?: string }): string | null {
	const title = (cfg.title || '').toLowerCase()
	if (title.includes('изображени') || title.includes('фото') || cfg.category === 'image') return '/bots/images'
	if (title.includes('видео') || cfg.category === 'video') return '/bots/video'
	if (title.includes('аудио') || title.includes('музык') || cfg.category === 'audio') return '/bots/audio'
	return null
}

// Admin-driven page sections с динамическим config'ом. Поля внутри config
// зависят от section_type (image vs video vs gallery vs cards и т.д.) и
// меняются админом без code-changes. `any` здесь honest — попытка типизировать
// каждую возможную shape создаёт false sense of safety + лавину type assertions.
interface PageSection {
	id: string
	section_type: string
	sort_order: number
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	config: Record<string, any>
	/** Разделы секции: `main` — рамка страницы, `tab:<слаг>` — блок вкладки (см. pages/home/homeBlocks.ts). */
	target_pages?: string[]
	/** false приезжает только в стафф-превью (?preview=1). */
	is_visible?: boolean
}

// State, который home передаёт в /chat при handleSend (text-категория).
interface ChatNavState {
	message: string
	autoSend: true
	preUploadedFiles?: Array<{
		url: string
		key?: string
		name: string
		mime_type: string
		isImage: boolean
	}>
}

// Card-shape из admin config (popular_category секция). Все поля optional —
// админ может оставить пустыми, fallback на defaults на use-site.
interface AdminCardConfig {
	id?: string
	title?: string
	description?: string
	background_url?: string
	// Лёгкое webp-превью баннера (≤512px) — героем карточки вместо оригинала.
	background_preview_url?: string | null
	/** 'droplet' — доп-стиль плитки «стекло-капля» (DropletGlass). */
	tile_style?: string
	target_adapter?: string
	target_model?: string
	system_prompt?: string
	target_pages?: string[]
	hidden_pages?: string[]
	settings?: GenerationCardSettings
	used_count?: number
	video_url?: string
	/** Постер видео (первый кадр, `<key>_preview.webp` от upload-video) —
	 *  плитка карточки без обложки. Атрибут poster: файла нет → как было. */
	video_preview_url?: string | null
	/** Лёгкий ролик-сосед для автоплея плитки (см. utils/cardVideoAutoplay). */
	video_loop_url?: string | null
	reference_image_url?: string
	user_image_locked?: boolean
	has_reference_video?: boolean
	/** Секунды видео тренда (замер сервера при плате за секунды входного видео) — цена плитки. */
	reference_video_seconds?: number | null
	has_reference_audio?: boolean
	reference_video_url?: string
	user_video_locked?: boolean
	reference_audio_url?: string
	user_audio_locked?: boolean
	price_override?: number
	/** Акция: скидочная цена + срок (ISO UTC) — эффективная цена на входе. */
	discount_price?: number
	discount_until?: string
	/** Наглядное демо (ba «до/после» | video) + гостевой флаг промпта. */
	demo_type?: 'ba' | 'video'
	demo_before_url?: string
	demo_after_url?: string
	demo_variants?: Array<{ key: string; label: string; after_url: string; prompt_patch: string; is_open?: boolean; placeholder?: string; description?: string; value_type?: 'text' | 'number'; min_value?: number | null; max_value?: number | null; user_input?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null }>
	card_kind?: CardKind
	/** Мульти-тренд: участники группы (бэк кладёт их внутрь плитки, из пула прячет). */
	members?: BackendCard[]
	user_input?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null
	photo_slots?: CardPhotoSlot[] | null
	hide_character?: boolean
	user_photo_limit?: number
	user_photo_hint?: string
	hide_end_image?: boolean
	end_image_hint?: string
	character_hint?: string
	prompt_locked?: boolean
	/** Пайплайн-карточка: slug шаблона (тренды «фото → видео» в GTM). */
	pipeline_slug?: string
	/** Этапы тренда: цены/акция для витрины (движком остаётся шаблон бэка). */
	pipeline?: TrendPipePricing
	/** Единый механизм мест: id секций «Лента трендов», где карточка показана. */
	feeds?: string[]
	/** Бейдж-тег плитки в лентах (текст + стиль viral|hit|cringe). */
	badge_text?: string
	badge_style?: string
	/** SEO-слаг карточки-инструмента — URL лендинга /generate?tool={slug}. */
	slug?: string
}

// Link-shape из admin config (footer_cta секция).
interface AdminLinkConfig {
	url?: string
	text?: string
}

interface Props {
	selectedModel: Model | null
	onSelectModel: (model: Model | null) => void
	pageSlug?: string
}

export default function HomeTemp({ selectedModel, onSelectModel, pageSlug = 'home' }: Props) {
	// selectedModel приходит из App-level state и заодно прокидывается в
	// App-level GenerateProvider (см. App.tsx). Используется здесь для
	// проверки в chat_input section — рендерить ли полноценный
	// GenerateInputBar (image/video/audio/preset) или простую textarea
	// (text, либо когда модель ещё не выбрана).
	const [sections, setSections] = useState<PageSection[]>([])
	// Пока секции едут, тело страницы пустое — видны только шапка и подвал
	// (скриншот владельца 13.09). Держим место заглушкой.
	const [sectionsLoading, setSectionsLoading] = useState(true)
	const [message, setMessage] = useState('')
	const [attachedFile, setAttachedFile] = useState<File | null>(null)
	const [attachedPreview, setAttachedPreview] = useState<string | null>(null)
	const [isUploading, setIsUploading] = useState(false)
	const homeFileInputRef = useRef<HTMLInputElement>(null)
	const navigate = useNavigate()
	// Гость: регистрация окном поверх главной — набранный запрос и выбранная плитка остаются на месте
	const openAuth = useOpenAuth()
	const { user } = useAuth()
	const { brands, allModels } = useProviders()
	const { selectModel, setPrompt, setPendingSettings, setPendingModelId } = useSelectedModel()
	const { intents, defaultModels, selected: selectedIntent, selectIntent } = useWelcomeIntent()

	// IntentType → ModelCategory из widgetResolver. Хранится здесь чтобы
	// не плодить mapping'и в нескольких файлах: «текст» приветственного
	// выбора это `text` категория модели, и т.д.
	const INTENT_TO_CATEGORY: Record<IntentType, ModelCategory> = {
		text: 'text',
		image: 'image',
		video: 'video',
		audio: 'audio',
		presentation: 'presentation',
	}

	// findModelById с alias-резолюцией. welcome-config хранит каноническую
	// короткую форму (`animagine-xl-v4`), а `/providers` отдаёт вендорскую
	// длинную (`aisha-ai-official/animagine-xl-v4-opt`) — без этого
	// резолвера плитка терялась бы для replicate-моделей с алиасом.
	// resolveModelIdAliases синхронен с backend MODEL_SETTINGS_ALIASES.
	const findModelById = (modelId: string): Model | null => {
		if (!modelId) return null
		const candidates = resolveModelIdAliases(modelId)
		for (const id of candidates) {
			const found = allModels.find((m) => m.id === id || m.backendModel === id)
			if (found) return found
		}
		return null
	}

	// Клик на model-tile в brand_selector — выставляем intent + модель,
	// остаёмся на главной. Виджет покажет hint вверху с выбранной моделью,
	// юзер вводит сообщение и жмёт Send. Routing уже на стороне handleSend.
	//
	// Для audio/presentation дополнительно прокидываем модель в widgetResolver
	// (saveLastModel) — когда юзер позже нажмёт Send и поедет в /bots/audio
	// или /bots/slides, виджет подхватит именно эту модель (а не дефолт).
	// Ресинк модели под восстановленный интент. Интент живёт в sessionStorage
	// и переживает F5, а App при старте сбрасывает НЕ-текстовую selectedModel
	// в null (guard в App.tsx restore — для чат-селектора). Итог после
	// перезагрузки: пилюля интента есть, а модель/цена/доп-поля/brand-аура —
	// нет. Возвращаем модель интента, как это делает клик по тайлу.
	// Уход на не-text интент: чат-вложение (скрепка) больше не применимо —
	// скрепка скрыта, а handleSend для медиа attachedFile игнорирует. Чистим,
	// чтобы не висел осиротевший чип превью над textarea (Bug 2).
	useEffect(() => {
		if (selectedIntent && selectedIntent.type !== 'text' && attachedFile) {
			setAttachedFile(null)
			if (attachedPreview) {
				URL.revokeObjectURL(attachedPreview)
				setAttachedPreview(null)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedIntent])

	useEffect(() => {
		// Guard от гонки при уходе с главной (тот же паттерн, что в дефолт-
		// эффекте Generate.tsx): navigate() меняет window.location СИНХРОННО
		// (history.pushState), а unmount HomeTemp догоняет в следующем render-
		// цикле. Без guard'а клик по карточке стиля/видео (onSelectModel(модель
		// карточки) + navigate('/generate')) успевал перетереться этим ресинком
		// обратно в модель интента под виджетом — на /generate уезжала не
		// модель карточки, а «выбранная ранее на главной» (или дефолтная
		// image-модель, если интент был text).
		const homePath = pageSlug === 'home' ? '/' : `/${pageSlug}`
		if (typeof window !== 'undefined' && window.location.pathname !== homePath) return
		if (!selectedIntent || allModels.length === 0) return
		const wantCategory = INTENT_TO_CATEGORY[selectedIntent.type]
		if (selectedModel?.category === wantCategory) return
		const intentModel = selectedIntent.modelId ? findModelById(selectedIntent.modelId) : null
		const fallbackId = defaultModels[selectedIntent.type] ?? null
		const m = intentModel ?? (fallbackId ? findModelById(fallbackId) : null)
		if (m) onSelectModel(m)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedIntent, selectedModel, allModels.length])

	// Живая цена генерации из GenerateContext (тот же useGenerateCost, что
	// в /generate и /bots-виджете): пересчитывается при смене модели И любых
	// параметров (длительность/разрешение/звук/...). Никакого хардкода —
	// работает для любой модели, поставленной на главную через админку.
	const { currentCost, costPending, costIssue, uploadedImage, referenceImages, hasPendingUploads } = useGenerate()

	// FLIP-сворачивание доп-зон виджета (uploads + pills) при уходе на
	// text-интент: контент зон живёт в shared-контексте модели и пропадает
	// из DOM мгновенно — фиксируем текущую высоту ДО смены стейта и плавно
	// ведём её в 0 (transition в .home-collapse). Появление анимируют
	// существующие emerge-каскады (key-remount), уход — эта шторка.
	const uploadsCollapseRef = useRef<HTMLDivElement>(null)
	const pillsCollapseRef = useRef<HTMLDivElement>(null)
	const collapseZonesSmoothly = () => {
		for (const ref of [uploadsCollapseRef, pillsCollapseRef]) {
			const el = ref.current
			if (!el || el.offsetHeight === 0) continue
			el.style.height = `${el.offsetHeight}px`
			el.style.overflow = 'hidden'
			requestAnimationFrame(() => {
				el.style.height = '0px'
				el.style.opacity = '0'
			})
			window.setTimeout(() => {
				el.style.height = ''
				el.style.overflow = ''
				el.style.opacity = ''
			}, 380)
		}
	}

	const handleTileClick = (intent: WelcomeIntent, model: Model) => {
		if (intent.type === 'text' && selectedIntent && selectedIntent.type !== 'text') {
			collapseZonesSmoothly()
		}
		// Модель именно кликнутой плитки — в ряду может быть несколько плиток
		// одной категории с разными моделями.
		selectIntent(intent.type, model.id)
		onSelectModel(model)
		if (intent.type === 'audio' || intent.type === 'presentation') {
			saveLastModel(INTENT_TO_CATEGORY[intent.type], model.id)
		}
	}

	useSEO({
		title:
			pageSlug === 'main_temp' ? 'Umnik.AI — Песочница' : 'Umnik.AI — Все нейросети в одном месте',
		description:
			pageSlug === 'main_temp'
				? ''
				: 'Агрегатор нейросетей Umnik.AI — ChatGPT, Claude, Kling, Nano Banana и 100+ моделей ИИ. Без VPN, на русском, оплата картой РФ. Текст, картинки, видео в одном сервисе.',
		canonical: pageSlug === 'main_temp' ? 'https://umnik.ai/main_temp' : 'https://umnik.ai/',
	})

	// Стафф-превью (?preview=1), как на /bots: бэк отдаёт и скрытые блоки/вкладки — собрать и посмотреть
	// до включения глазика. Право проверяет бэк; у не-сотрудника параметр ничего не меняет.
	const location = useLocation()
	const isPreview = isStaff(user?.role) && new URLSearchParams(location.search).get('preview') === '1'

	useEffect(() => {
		// tabs=1 — вместе с рамкой приезжают блоки всех вкладок (разделы tab:<слаг>): вкладки переключаются
		// без запросов. Старый бэк параметр не знает и просто игнорирует.
		const query = new URLSearchParams()
		if (pageSlug === 'home') query.set('page', 'main')
		query.set('tabs', '1')
		if (isPreview) query.set('preview', '1')
		const pageParam = `?${query.toString()}`
		// Токен если есть → бэк отличает гостя (вырезает готовые промпты,
		// prompt_locked) от зареганого. Рефетч на смене user (логин/логаут).
		const token = api.getToken()
		fetch(`${API_BASE}/api/v1/admin/page-sections/public/page/${pageSlug}${pageParam}`,
			token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
			.then((r) => r.json())
			.then((data) => {
				if (data.ok) setSections(data.sections)
			})
			.catch(() => {})
			// finally, а не then: провал запроса тоже снимает заглушку — иначе
			// она осталась бы навсегда и выглядела как вечная загрузка.
			.finally(() => setSectionsLoading(false))
	}, [pageSlug, user, isPreview])

	// Scroll-fade orbs + atmosphere + 7 orbs JSX переехали в
	// components/GlobalAmbient.tsx (App-level mount, виден на всех страницах
	// кроме auth). HomeTemp больше не дублирует эту логику — DRY.

	const findModel = (adapter: string, modelId: string) => {
		for (const brand of brands) {
			for (const bm of brand.models) {
				if (bm.adapter === adapter && bm.id === modelId) {
					return brandModelToModel(brand, bm)
				}
			}
		}
		return null
	}

	const handleSend = async () => {
		// Для audio/presentation — message может быть пустой, но send всё равно
		// валидный (юзер уйдёт в widget и допишет промпт там). Для остальных
		// требуем непустое сообщение ИЛИ прикреплённый файл.
		const isMediaWidget =
			selectedIntent?.type === 'audio' || selectedIntent?.type === 'presentation'
		if (!isMediaWidget && !message.trim() && !attachedFile) return
		// Кнопка отправки disabled пока selectedIntent === null. Эта проверка
		// — defense-in-depth на случай если кто-то вызовет handleSend в обход
		// disabled-атрибута (напр. Enter с физической клавиатуры).
		if (!selectedIntent) return
		// Загрузка картинки ещё идёт (итем без url) — autoSend на /generate
		// ушёл бы без неё. Enter обходит disabled кнопки, поэтому guard здесь.
		if (isUploading || hasPendingUploads) return
		// Видео на входе ещё меряется (плата за секунды входного видео) — цены нет, запуск ждёт.
		if (costPending || costIssue) return

		const intentModel = selectedIntent.modelId
			? findModelById(selectedIntent.modelId)
			: null

		// Текст — на /chat. Для image/video — на /generate. Audio/presentation
		// — на /bots/audio и /bots/slides соответственно. Маршрут зашит в
		// WelcomeIntent.targetRoute, но здесь уже семантика по типу.
		if (selectedIntent.type === 'text') {
			// Если админ настроил text-модель — выбираем её перед navigate;
			// иначе fallback на текущий поведенческий дефолт (gpt-5.2).
			const fallback = findModel('openai', 'gpt-5.2')
			const model = intentModel || fallback
			if (model) onSelectModel(model)

			const state: ChatNavState = { message, autoSend: true }

			if (attachedFile && user) {
				try {
					setIsUploading(true)
					const uploaded = await uploadChatFile(attachedFile)
					state.preUploadedFiles = [{
						url: uploaded.url,
						key: uploaded.key,
						name: uploaded.name,
						mime_type: uploaded.mime_type,
						isImage: attachedFile.type.startsWith('image/'),
					}]
				} catch {
					// продолжаем без файла
				} finally {
					setIsUploading(false)
				}
			}

			setAttachedFile(null)
			if (attachedPreview) {
				URL.revokeObjectURL(attachedPreview)
				setAttachedPreview(null)
			}
			navigate('/chat', { state })
			return
		}

		// audio/presentation → специализированный widget (/bots/audio,
		// /bots/slides). Модель уже зафиксирована через saveLastModel в
		// handleTileClick — widget подхватит её при mount'е через
		// resolveLastUsedModel. Промпт пока не пробрасываем: эти виджеты
		// принимают prompt через свой собственный UI (другие поля, не
		// просто textarea). Если юзер успел что-то ввести — оно остаётся
		// в state.message как preserve для будущей интеграции, но widget
		// его на сегодня не читает.
		if (selectedIntent.type === 'audio' || selectedIntent.type === 'presentation') {
			navigate(selectedIntent.type === 'audio' ? '/bots/audio' : '/bots/tools/slides')
			return
		}

		// image/video → /generate. Generate подхватит selectedModel из App-state
		// и prompt из SelectedModelContext (см. GenerateContext:181, ctxPrompt).
		if (intentModel) onSelectModel(intentModel)
		setPrompt(message)
		// autoSend: приёмник в Generate.tsx (useEffect location.state.autoSend)
		// сам вызовет handleGenerate() — юзер не жмёт «Создать» второй раз.
		// Провайдер общий App-level: prompt/uploads/settings уже в контексте.
		//
		// showHint (зелёный пилл «Добавьте фото…») — только если модель
		// принимает изображение И поле ещё пусто: при заполненном поле
		// генерация уже пошла и подсказка лишняя. «Пусто» проверяем в ОБОИХ
		// стейтах: single-модели держат картинку в uploadedImage, multi-image
		// (nano-banana и пр.) — в referenceImages. Пути переиспользования
		// карточек/стилей шлют свой showHint как раньше — их не трогаем.
		navigate('/generate', {
			state: {
				showHint: intentModel
					? getModelConfig(intentModel).showImageUpload &&
						!uploadedImage &&
						referenceImages.length === 0
					: false,
				autoSend: true,
			},
		})
	}

	// Admin-карточка → item витрины. ЕДИНЫЙ маппер для сетки «Популярного» и
	// лент трендов (trend_feed): цена = computeModelCost по settings карточки
	// с floor-оверрайдом/акцией (то, что спишет бэк), все поля пина.
	const adminCardToItem = (card: AdminCardConfig, i: number) => {
		const realModel =
			card.target_adapter && card.target_model
				? findModel(card.target_adapter, card.target_model)
				: null
		// Акция: эффективная цена = промо КАК ЕСТЬ (= что спишет бэк) — со
		// скидками-уровнями промо честно НИЖЕ базы модели; без акции — floor.
		let discount = activeDiscount(card.price_override, card.discount_price, card.discount_until)
		// Гейт промо (зеркало бэка effective_source_price): скидка действует на
		// карточку с ЕЁ пресетом. Параметры дороже обычной цены (админ сменил
		// их после назначения акции) → бэк спишет расчёт, витрина тоже.
		const modelCost = computeModelCost(realModel, card.settings, cardInputVideoContext(realModel, card))
		const cardBase = card.price_override ?? modelCost
		if (discount && modelCost > cardBase) discount = null
		let cardCost = discount ? discount.price : Math.max(modelCost, card.price_override ?? 0)
		// Обычная цена ДО акции — зачёркнутый ценник промо-стикера плитки.
		let cardRegular = discount ? Math.max(modelCost, card.price_override ?? 0) : 0
		// Тренд: цена «под ключ» = оба этапа; акция этапов (−N% до даты) даёт
		// промо-сумму и плашку. Формула промо = та, по которой бэк спишет.
		// Цена зависит ТОЛЬКО от pipeline.enabled + цен этапов (как в админ-
		// превью) — НЕ от pipeline_slug: слаг нужен для запуска, не для показа
		// цены (иначе цена пропадала до синка шаблона / у слайдов).
		const pipe = card.pipeline
		if (pipe?.enabled) {
			discount = trendDiscount(pipe)
			cardCost = trendTotalCost(pipe)
			cardRegular = discount ? (pipe.photo?.credits || 0) + (pipe.video?.credits || 0) : 0
		}
		const item: PopularModelCardType = {
			id: card.id || `card-${i}`,
			title: card.title || '',
			description: card.description || '',
			backgroundImageUrl: effectiveCardCover(card),
			backgroundPreviewUrl: card.background_preview_url || null,
			tileStyle: card.tile_style || undefined,
			modelLogoUrl: realModel?.icon || '',
			creditsLabel: realModel || (pipe?.enabled && cardCost > 0) ? String(cardCost) : '',
			rating: realModel?.rating || 0,
			reviews: realModel?.users || 0,
			targetAdapter: card.target_adapter || '',
			targetModelId: card.target_model || '',
			defaultPrompt: card.system_prompt || '',
			settings: card.settings,
			usedCount: card.used_count,
			videoUrl: card.video_url,
			videoPosterUrl: card.video_preview_url || undefined,
			videoLoopUrl: card.video_loop_url || undefined,
			// Медиа-референсы «под капотом» + локи полей юзера — уедут в пин
			// (attachFromPopularCard) → GTM на /generate.
			referenceImageUrl: card.reference_image_url,
			userImageLocked: card.user_image_locked,
			referenceVideoUrl: card.reference_video_url,
			hasRefVideo: card.has_reference_video,
			hasRefAudio: card.has_reference_audio,
			refVideoSeconds: card.reference_video_seconds,
			userVideoLocked: card.user_video_locked,
			referenceAudioUrl: card.reference_audio_url,
			userAudioLocked: card.user_audio_locked,
			priceOverride: discount?.price ?? card.price_override,
			discountPercent: discount?.percent,
			discountLeftLabel: discount?.leftLabel,
			discountUntil: discount?.until,
			creditsOldLabel: discount && cardRegular > cardCost ? String(cardRegular) : undefined,
			regularPrice: discount && cardRegular > 0 ? cardRegular : undefined,
			// Демо «до/после»/видео + гостевой флаг — в пин (демо-зона GTM).
			demoType: card.demo_type,
			demoBeforeUrl: card.demo_before_url,
			demoAfterUrl: card.demo_after_url,
			demoVariants: card.demo_variants,
			cardKind: card.card_kind,
			// Мульти-тренд: свотчи участников на плитке; клик ведёт на лендинг группы (там выбор участника).
			trendMembers: trendMembersOf(card).map(trendMemberPreview),
			primaryUrl: cardPrimaryUrl({
				id: card.id || '', title: card.title || '', slug: card.slug,
				target_pages: card.target_pages, hidden_pages: card.hidden_pages, card_kind: card.card_kind,
			}),
			userInput: card.user_input,
			photoSlots: card.photo_slots,
			hideCharacter: card.hide_character,
			hideEndImage: card.hide_end_image,
			endImageHint: card.end_image_hint,
			characterHint: card.character_hint,
			userPhotoLimit: card.user_photo_limit,
			userPhotoHint: card.user_photo_hint,
			promptLocked: card.prompt_locked,
			pipelineSlug: card.pipeline_slug,
			// Слаг — для SEO-навигации: клик уводит на /generate?tool={slug}
			// (путь карточки попадает в адресную строку, как на гридах/трендах).
			slug: card.slug,
			disabled: !card.target_adapter && !card.pipeline_slug,
		}
		return { item, cardCost, realModel }
	}

	// Мульти-тренд (23.10): клик по кадру версии на плитке — лендинг группы (или генератор по адресу карточки)
	// сразу с этой версией.
	const handleMemberClick = (item: PopularModelCardType, memberId: string) => {
		if (item.primaryUrl) navigate(item.primaryUrl, { state: { trendMemberId: memberId, skipAnchor: true } })
		else if (item.slug) navigate(`/generate?tool=${encodeURIComponent(item.slug)}`, { state: { trendMemberId: memberId } })
	}

	const handleCardClick = (item: PopularModelCardType) => {
		// Мульти-тренд (22.10): группу саму в виджет не кладём — у неё нет промпта. Её лендинг в разделе применяет
		// выбранного участника и рисует ряд участников; без лендинга (нет слага) кликать некуда.
		if (item.cardKind === 'multitrend') {
			// Группа без раздела (только «Главная») — в генератор по адресу карточки: deep-link применит участника.
			if (item.primaryUrl) navigate(item.primaryUrl)
			else if (item.slug) navigate(`/generate?tool=${encodeURIComponent(item.slug)}`)
			return
		}
		const model =
			item.targetAdapter && item.targetModelId
				? findModel(item.targetAdapter, item.targetModelId)
				: null
		// Пин «Выбранный стиль» + атрибуция генераций — единый канал
		// (маппинг полей живёт в конвертере, см. api/attachedStyleCard).
		// toolMode — то же правило, что у «Популярных» на /bots: карточки
		// image/video открывают фокус-режим инструмента на /generate
		// (промпт работает под капотом, а не светится в баре).
		attachFromPopularCard(item, {
			toolMode: model ? model.category === 'image' || model.category === 'video' : !!item.pipelineSlug,
		})
		selectModel(item)
		if (model && onSelectModel) onSelectModel(model)

		if (model?.category === 'text') {
			navigate('/chat', { state: { systemPrompt: item.defaultPrompt || '' } })
		} else if (item.slug) {
			// Путь карточки — в адресную строку (/generate?tool={slug}):
			// копируется/шарится, единый SEO-канал с гридами/трендами/
			// «Приложениями». Пин уже выставлен выше (мгновенный tool-режим),
			// поэтому state.toolApplied говорит deep-link-эффекту /generate НЕ
			// переприменять карточку (без лишнего фетча/мигания) — он лишь
			// оставляет ?tool в URL.
			navigate(`/generate?tool=${encodeURIComponent(item.slug)}`, {
				state: { showHint: getModelConfig(model).showImageUpload, toolApplied: item.slug },
			})
		} else {
			// Легаси-фолбэк без слага: модель через `?model=` (onSelectModel выше
			// молча пропускается, если findModel не нашёл модель в момент клика —
			// каталог brands ещё грузится; deep-link-эффект /generate выберет её,
			// как только каталог готов, и уберёт query из URL).
			const q = item.targetModelId ? `?model=${encodeURIComponent(item.targetModelId)}` : ''
			navigate(`/generate${q}`, { state: { showHint: getModelConfig(model).showImageUpload } })
		}
	}

	const renderSection = (section: PageSection) => {
		const cfg = section.config

		switch (section.section_type) {
			case 'hero_text':
				return (
					<section
						key={section.id}
						className="hero-section"
						style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 10 }}
					>
						<h1 className="hero-title" dangerouslySetInnerHTML={{ __html: cfg.h1 || '' }} />
						{cfg.subtitle && <p className="hero-subtitle">{cfg.subtitle}</p>}
					</section>
				)

			case 'brand_selector': {
				// 5 model-tiles из welcome-config: по одной плитке на категорию
				// (text/image/video/audio/presentation), каждая привязана к
				// конкретной модели из админки (platform_settings → admin UI).
				// Раньше тут был hardcoded DEFAULT_HERO_BRANDS — заменено на
				// driven-from-backend список, чтобы добавление модели в админку
				// автоматом появлялось на главной (одна точка правды).
				//
				// detectBrand резолвит brand-цвет/иконку из (provider, model.id).
				// Если default_model[type] не задан в админке (modelId === null)
				// или модель отсутствует в allModels (выключена / переименована)
				// — карточка для этой категории не рендерится. Юзер не видит
				// «битую» плитку, всё что показано — реально кликабельно.
				type TileEntry = {
					intent: WelcomeIntent
					model: Model
					brand: ReturnType<typeof detectBrand>
				}
				// Состав и порядок ряда из конфига секции (config.tiles, задаётся в
				// конструкторе страниц): произвольное число плиток, категория плитки
				// наследует поведение клика (маршрут/плейсхолдер). Пока tiles не
				// настроен — прежний ряд: по одной плитке на категорию из
				// welcome-config (дефолт-модели).
				const cfgTiles: { category?: string; model_id?: string }[] =
					Array.isArray(cfg.tiles) ? cfg.tiles : []
				const tileEntries: TileEntry[] = (
					cfgTiles.length
						? cfgTiles.map((t): TileEntry | null => {
								const intent = intents.find((i) => i.type === t.category) || null
								const modelId = (t.model_id || '').trim() || null
								if (!intent || !modelId) return null
								const model = findModelById(modelId)
								if (!model) return null
								return { intent, model, brand: detectBrand(model.provider, model.id) }
							})
						: intents.map((intent): TileEntry | null => {
								const modelId = defaultModels[intent.type] ?? null
								if (!modelId) return null
								const model = findModelById(modelId)
								if (!model) return null
								const brand = detectBrand(model.provider, model.id)
								return { intent, model, brand }
							})
				).filter((e): e is TileEntry => e !== null)

				// Для «папки»: 3 brand'а которых НЕТ среди welcome-tiles —
				// показываем как намёк что в каталоге больше моделей. На hover
				// карточки выезжают (анимация в SCSS).
				const usedBrandIds = new Set(
					tileEntries.map((e) => e.brand?.id).filter(Boolean) as string[]
				)
				const folderPreviewBrands = brands
					.filter((b) => !usedBrandIds.has(b.id))
					.slice(0, 3)

				// Динамический счётчик всех нейросетей — сумма моделей по
				// всем brand'ам из useProviders (берёт enabled-only из API).
				// При добавлении/удалении моделей админкой — пересчитывается
				// автоматически без правки кода.
				const totalModels = brands.reduce((sum, b) => sum + b.models.length, 0)

				return (
					<div key={section.id} className="bot-selector">
						{tileEntries.map(({ intent, model, brand }) => {
							// Активность по модели, не только категории: в ряду может быть
							// несколько плиток одной категории (config.tiles) — подсвечиваем
							// именно кликнутую (modelId пишется в интент при клике).
							const isActive =
								selectedIntent?.type === intent.type &&
								(selectedIntent.modelId ? selectedIntent.modelId === model.id : true)
							// Fallback на нейтральную иконку если brand не задетектился
							// (новая модель без entry в data/brands.ts).
							const iconPath = brand?.icon || '/icons/default.svg'
							const iconName = brand?.name || model.name
							return (
								<button
									key={`${intent.type}:${model.id}`}
									type="button"
									className={`bot-tile${isActive ? ' bot-tile--active' : ''}`}
									data-brand={brand?.id ?? ''}
									data-intent={intent.type}
									onClick={() => handleTileClick(intent, model)}
									aria-pressed={isActive}
									aria-label={`${intent.label}: ${model.name}`}
								>
									<span className="bot-tile__icon">
										<ModelIcon icon={iconPath} name={iconName} size={30} />
									</span>
									<span className="bot-tile__name">{model.name}</span>
								</button>
							)
						})}
						{cfg.link_text && (
							<button
								type="button"
								className="bot-tile bot-folder"
								onClick={() => navigate(cfg.link_url || '/bots')}
								aria-label={cfg.link_text}
							>
								<span className="bot-folder__stage">
									{folderPreviewBrands.map((brand, i) => (
										<span
											key={brand.id}
											className={`bot-folder__preview bot-folder__preview--${i + 1}`}
										>
											<img src={brand.icon} alt="" aria-hidden="true" />
										</span>
									))}
									<span className="bot-folder__pocket" />
									{/* Счётчик ПОВЕРХ pocket'а (z > pocket z-2),
									    label рендерится только если данные подгрузились
									    (totalModels > 0) — иначе пустая папка во время
									    initial fetch. */}
									{totalModels > 0 && (
										<span className="bot-folder__count">{totalModels}</span>
									)}
								</span>
								<span className="bot-tile__name">{cfg.link_text}</span>
							</button>
						)}
					</div>
				)
			}

			case 'chat_input': {
				// Активный intent (если выбран) — показываем компактный hint
				// в шапке виджета: иконка + лейбл категории + имя модели.
				// Выбор делается кликом на model-tile в brand_selector ниже
				// (см. case 'brand_selector'). Пока intent не выбран — hint
				// скрыт, send-кнопка disabled.
				const activeIntent = selectedIntent
					? intents.find((i) => i.type === selectedIntent.type) || null
					: null
				const ActiveIcon = activeIntent
					? ((LucideIcons as unknown as Record<string, React.ComponentType<{ size?: number }>>)[activeIntent.iconName] || LucideIcons.Square)
					: null
				// Резолвим human-friendly имя модели (вместо raw modelId типа
				// "gamma-presentation"). Если модель не найдена (выключена /
				// переименована в админке) — fallback на raw modelId, не
				// ломаем UI.
				const activeModelName = selectedIntent?.modelId
					? findModelById(selectedIntent.modelId)?.name ?? selectedIntent.modelId
					: null

				return (
					<div key={section.id} className="chat-input-wrapper">
						{activeIntent && (
							<div className="welcome-intent-hint">
								{ActiveIcon && <ActiveIcon size={14} />}
								<span>{activeIntent.label}</span>
								{activeModelName && (
									<span className="welcome-intent-hint__model">· {activeModelName}</span>
								)}
							</div>
						)}
						{/* Живая цена в углу виджета: currentCost из GenerateContext
						    реагирует на параметры генерации (pills/слайдеры).
						    key={currentCost} ре-маунтит бейдж → pop-анимация
						    сигналит пересчёт. */}
						{selectedIntent && currentCost > 0 && !costPending && !costIssue && (
							<span
								className="home-widget-cost"
								key={currentCost}
								title={`${currentCost} ${pluralizeTokens(currentCost)}`}
							>
								<Coin size={13} />
								{currentCost}
							</span>
						)}
						{attachedFile && (
							<div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text-secondary, #aaa)' }}>
								{attachedPreview ? (
									<img src={attachedPreview} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover' }} />
								) : (
									<span>📎</span>
								)}
								<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{attachedFile.name}</span>
								<button
									type="button"
									onClick={() => {
										setAttachedFile(null)
										if (attachedPreview) { URL.revokeObjectURL(attachedPreview); setAttachedPreview(null) }
									}}
									style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16 }}
								>✕</button>
							</div>
						)}
						{/* Upload-слоты модели (image / video / swap / end-image / reference-*)
						    из shared UploadsRow — тот же что на /generate и /bots/*.
						    Появляются только для не-text моделей и только когда у модели
						    есть upload-флаги (requiresImage / supportsImageInput / etc).
						    key={model.id} → re-mount при смене модели → CSS-stagger
						    animation slide-from-top, синхронно с pills внизу. */}
						<div ref={uploadsCollapseRef} className="home-collapse">
						{selectedIntent && selectedIntent.type !== 'text' && selectedModel && (
							<div
								className={selectedIntent.type === 'presentation' ? 'home-preset-host' : 'home-uploads-row'}
								key={selectedModel.id}
							>
								{/* hidePresentationInfo: на home юзер выбрал конкретный intent
								    через welcome-tile, все настройки Gamma — в пилюлях; инфо-
								    плейсхолдер PresentationControls тут лишний. Тумблер Create/Edit
								    убран 10.09 — модель правок скрыта в админке. */}
								<UploadsRow hidePresentationInfo />
							</div>
						)}
						</div>
						<textarea
							className="chat-input"
							placeholder={(() => {
									// Resolution order (см. INTENT_PLACEHOLDERS наверху файла):
									//   1. selectedModel.paramHints.prompt.description (admin)
									//   2. INTENT_PLACEHOLDERS[intent.type] (категория)
									//   3. cfg.placeholder (legacy page-section)
									//   4. Дефолт
									// eslint-disable-next-line @typescript-eslint/no-explicit-any
									const modelHint = (selectedModel as any)?.paramHints?.prompt?.description
									const intentPlaceholder = selectedIntent
										? INTENT_PLACEHOLDERS[selectedIntent.type]
										: null
									return (
										modelHint ||
										intentPlaceholder ||
										cfg.placeholder ||
										'Что хочешь узнать или обсудить?'
									)
								})()}
							rows={2}
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							onKeyDown={(e) => {
								if (shouldSendOnEnter(e)) {
									e.preventDefault()
									void handleSend()
								}
							}}
						/>
						<div className="chat-input-actions">
							<input
								ref={homeFileInputRef}
								type="file"
								accept="image/*,.pdf,.txt,.doc,.docx"
								style={{ display: 'none' }}
								onChange={(e) => {
									const file = e.target.files?.[0]
									if (!file) return
									setAttachedFile(file)
									setAttachedPreview(file.type.startsWith('image/') ? URL.createObjectURL(file) : null)
									if (homeFileInputRef.current) homeFileInputRef.current.value = ''
								}}
							/>
							{/* Скрепка — только для text-интента (или пока интент не
							    выбран). Для image/video/presentation отдельное поле
							    референс-файлов даёт UploadsRow выше, а handleSend для
							    медиа attachedFile вообще не использует — скрепка там
							    была мёртвой и путала юзера (Bug 2). */}
							{(!selectedIntent || selectedIntent.type === 'text') && (
								<button type="button" className="attach-btn" onClick={() => homeFileInputRef.current?.click()}>
									<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
										<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
									</svg>
								</button>
							)}
							{/* Pills с настройками модели (Формат/Разрешение/Длительность/
							    Suno-controls/PresentationPills/HeyGen-pills и т.д.) — тот же
							    компонент что на /generate (см. SettingsPillsRow.tsx).
							    Появляются только для не-text моделей (image/video/audio/
							    presentation). При смене модели key={modelId} триггерит
							    re-mount → CSS-stagger анимация «выпрыгивания из скрепки».
							    Для text-моделей / без модели → null, лейаут как был. */}
							<div ref={pillsCollapseRef} className="home-collapse home-collapse--pills">
							{selectedIntent && selectedIntent.type !== 'text' && selectedModel && (
								<div
									className="home-pills-row"
									key={selectedModel.id}
								>
									<SettingsPillsRow />
								</div>
							)}
							</div>
							{/* Для unauthenticated добавляем CTA-текст «Попробовать бесплатно»
							    + клик ведёт на /register (а не handleSend). Логика: новый
							    юзер не может отправлять сообщения — сначала регистрация.
							    Поэтому кнопка всегда кликабельна (disabled=false), даже
							    если поле пустое — это её отдельная роль как CTA.
							    Для авторизованных — обычная круглая send-кнопка как было. */}
							<button
								className={`send-btn${!user ? ' send-btn--cta' : ''}`}
								// data-has-content включает breathing animation в CSS
								// ТОЛЬКО когда юзер реально может что-то отправить
								// (есть текст ИЛИ файл). Без этого breathing крутилась
								// бы 24/7 на audio/preset моделях где prompt опционален —
								// визуально-усталостно и не сигналит «нажми меня прямо
								// сейчас» о готовности.
								data-has-content={
									(!!message.trim() || !!attachedFile) ? 'true' : 'false'
								}
								disabled={
									user
										? (
											// Send disabled пока юзер не выбрал intent через
											// brand_selector ниже. Иначе нечего отправлять —
											// мы не знаем какая модель и куда роутить. Для
											// audio/presentation сообщение опционально (виджет
											// принимает promp у себя), поэтому там разрешаем
											// и пустой message.
											!selectedIntent ||
											isUploading ||
											// Виджет-загрузки (UploadsRow) живут в GenerateContext,
											// локальный isUploading (чат-вложение) их не видит.
											hasPendingUploads ||
											costPending ||
											!!costIssue ||
											((selectedIntent.type !== 'audio' &&
												selectedIntent.type !== 'presentation') &&
												!message.trim() &&
												!attachedFile)
										)
										: false
								}
								onClick={() => {
									if (!user) {
										openAuth('/register')
										return
									}
									void handleSend()
								}}
							>
								{!user && !isUploading && (
									<span className="send-btn__label">Попробовать бесплатно</span>
								)}
								{isUploading || hasPendingUploads ? (
									<svg className="spin" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
										<path d="M21 12a9 9 0 11-6.219-8.56" />
									</svg>
								) : (
									<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
										<path d="M12 19V5M5 12l7-7 7 7" />
									</svg>
								)}
							</button>
						</div>
					</div>
				)
			}

			// case 'assistants' удалён: слайдер агентов давно снят с главной
			// (рендерился в return null), а его хвост держал ЛИШНИЙ fetch
			// каталога агентов на каждой загрузке главной. Каталог живёт на
			// /bots/assistants и в мини-чате; админка — «ИИ-агенты» в сайдбаре.
			// case 'popular' (legacy-баннеры) удалён ещё раньше —
			// использовался только popular_category ниже.

			case 'video_showcase': {
				// Гейт промо — как в лентах: плашка «−N%» не обещает скидку,
				// которой не будет (параметры айтема дороже его цены).
				const videos = (Array.isArray(cfg.videos) ? cfg.videos : []).map((v: PopularVideoItem) => {
					const m = v.target_adapter && v.target_model ? findModel(v.target_adapter, v.target_model) : null
					return gatePromoByCost(v, computeModelCost(m, v.settings, cardInputVideoContext(m, v)))
				})
				return (
					<PopularVideoShowcase
						key={section.id}
						title={cfg.title}
						videos={videos}
						onVideoClick={(video) => {
							// Пайплайн-слайду модель не нужна (этапы ведёт GTM по slug'у).
							if (!video.pipeline_slug && (!video.target_adapter || !video.target_model)) return
							const model = video.target_adapter && video.target_model
								? findModel(video.target_adapter, video.target_model)
								: null
							// Пин «Выбранный стиль» (+атрибуция по id у новых айтемов);
							// обложкой пина станет первый кадр ролика. Для image/video-
							// моделей — фокус-режим инструмента (GTM), ролик — демо.
							// Акция: эффективная цена подменяет оверрайд (= спишет бэк).
							const discount = activeDiscount(video.price_override, video.discount_price, video.discount_until)
							attachFromVideoItem(video, {
								toolMode: model?.category === 'image' || model?.category === 'video' || !!video.pipeline_slug,
								priceOverride: discount?.price ?? video.price_override,
								discountUntil: discount?.until,
								regularPrice: discount ? video.price_override : undefined,
							})
							if (model && onSelectModel) onSelectModel(model)
							if (video.system_prompt) setPrompt(video.system_prompt)
							// Пресет параметров карточки → виджет /generate (как у popular_category).
							// Канал pendingSettings подхватит GenerateContext при маунте.
							setPendingSettings(video.settings ?? null)
							setPendingModelId(video.target_model || null)
							if (model?.category === 'text') {
								navigate('/chat', { state: { systemPrompt: video.system_prompt || '' } })
							} else {
								// см. handleCardClick: страховка от гонки findModel/brands.
								// Пайплайн-слайд идёт без ?model= (модель этапов — в шаблоне).
								const q = video.target_model ? `?model=${encodeURIComponent(video.target_model)}` : ''
								navigate(`/generate${q}`, {
									state: { showHint: getModelConfig(model).showImageUpload },
								})
							}
						}}
					/>
				)
			}

			case 'trend_feed': {
				// Лента трендов — «место» единого пула: участники отмечают её в
				// feeds[] карточки, лента лишь рендерит своим видом (wide|reels).
				// Пул — cards секции «Популярное» этой же выдачи.
				const pool: AdminCardConfig[] = (() => {
					const pop = sections.find((s) => s.section_type === 'popular_category')
					const cards = pop?.config?.cards
					return Array.isArray(cards) ? (cards as AdminCardConfig[]) : []
				})()
				// Членство в ленте = ТОЛЬКО feeds[] (ровно как админ-превью ленты,
				// см. MainSettings.feedMembers). Лейны/глазики (target_pages/
				// hidden_pages) управляют СЕТКОЙ «Популярного» и не выкидывают
				// карточку из ленты: «скрыл из раздела → пропала и из ленты» был
				// баг связки. Убрать из ленты = снять пилюлю ленты в карточке.
				// Гейт промо: плашка «−N%» не обещает скидку, которой не будет
				// (параметры карточки дороже её цены — бэк спишет расчёт).
				// Порядок — свой у ленты (админ: перетаскивание плиток блока →
				// config.card_order), как на /bots/trends. Режимы «по популярности»/
				// «перемешивать» задают порядок сами — их главная не применяет,
				// поэтому и свой порядок берём только в дефолтном режиме.
				const membersRaw = pool
					.filter((card) => (card.feeds || []).includes(section.id))
					.map((card) => {
						const m = card.target_adapter && card.target_model ? findModel(card.target_adapter, card.target_model) : null
						return gatePromoByCost(card, computeModelCost(m, card.settings, cardInputVideoContext(m, card)))
					})
				const members = String(cfg.order_mode || 'admin') === 'admin'
					? applyFeedCardOrder(membersRaw, cfg.card_order)
					: membersRaw
				if (!members.length) return null
				return (
					<TrendFeedSection
						key={section.id}
						title={String(cfg.title || '')}
						subtitle={cfg.subtitle ? String(cfg.subtitle) : undefined}
						icon={cfg.icon ? String(cfg.icon) : undefined}
						layout={cfg.layout === 'wide' ? 'wide' : 'reels'}
						hoverAction={cfg.hover_action === true}
						cards={members}
						onPick={(c, i) => handleCardClick(adminCardToItem(c as AdminCardConfig, i).item)}
						onPickMember={(c, memberId, i) => handleMemberClick(adminCardToItem(c as AdminCardConfig, i).item, memberId)}
					/>
				)
			}

			case 'popular_category': {
				const allCards: AdminCardConfig[] = Array.isArray(cfg.cards) ? (cfg.cards as AdminCardConfig[]) : []
				const configCards = allCards.filter((card) =>
					(!card.target_pages?.length || card.target_pages.includes('main')) &&
					!card.hidden_pages?.includes('main'),
				)
				if (configCards.length === 0) return null

				return (
					<section
						key={section.id}
						className={styles.section}
						style={{
							margin: '0 auto',
							display: 'flex',
							flexDirection: 'column',
							gap: '16px',
							maxWidth: '1200px',
							width: '100%',
							overflow: 'hidden',
							padding: '20px 0',
							position: 'relative',
							zIndex: 10,
						}}
					>
						<div className={styles.header}>
							<h2 className={styles.h2}>{cfg.title || ''}</h2>
							{getCategoryRoute(cfg) && (
								<a href={getCategoryRoute(cfg)!} className={styles.allBtn}>
									Все стили ›
								</a>
							)}
						</div>
						{/* Ряд листается колесом и протяжкой мыши: скроллбар скрыт,
						    на Windows-мыши его иначе нечем прокрутить. */}
						<DragScrollRow className={styles.row}>
							{configCards.map((card, i) => {
								const { item, cardCost, realModel } = adminCardToItem(card, i)
								const showStats = cfg.show_stats !== false
								return (
									<PopularModelCard
										key={`${item.id}-${i}`}
										item={item}
										realCost={showStats ? cardCost : undefined}
										realRating={showStats ? realModel?.rating : undefined}
										realUsers={showStats ? realModel?.users : undefined}
										onClick={() => handleCardClick(item)}
										onPickTrendMember={(memberId) => handleMemberClick(item, memberId)}
										showCta
									/>
								)
							})}
						</DragScrollRow>
					</section>
				)
			}

			case 'faq': {
				const faqItems: Array<{ question: string; answer: string }> = cfg.items || []
				// FAQPage JSON-LD из тех же админ-данных — шанс на rich-сниппет
				// в выдаче. \u003c — чтобы '</script>' в ответе не рвал HTML.
				const faqLd = faqItems.length
					? JSON.stringify({
							'@context': 'https://schema.org',
							'@type': 'FAQPage',
							mainEntity: faqItems.map((it) => ({
								'@type': 'Question',
								name: it.question,
								acceptedAnswer: { '@type': 'Answer', text: it.answer },
							})),
						}).replace(/</g, '\\u003c')
					: null
				return (
					<Fragment key={section.id}>
						{faqLd && (
							<script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqLd }} />
						)}
						<FaqAccordion
							items={faqItems}
							subtitle={cfg.subtitle || 'Поддержка'}
							title={cfg.title || 'FAQ'}
							defaultOpenIndex={0}
						/>
					</Fragment>
				)
			}

			case 'footer_cta':
				return (
					<div key={section.id} className="footer-cta">
						<h3 className="footer-cta-title">{cfg.title || ''}</h3>
						<p className="footer-cta-subtitle">{cfg.subtitle || ''}</p>
						<div className="footer-cta-links">
							{Array.isArray(cfg.links) &&
								(cfg.links as AdminLinkConfig[]).map((link, i) => (
									<a key={i} href={link.url} className="footer-cta-link">
										{link.text}
									</a>
								))}
						</div>
					</div>
				)

			// ── Конструктор главной (pages/home): доп. навигация и блоки вкладок ──
			case 'tabs':
				return (
					<HomeTabs
						key={section.id}
						section={section as HomeSection}
						sections={sections as HomeSection[]}
						renderBlock={renderTabBlock}
						isPreview={isPreview}
					/>
				)

			// «Витрина» может стоять и в общем потоке страницы, вне вкладок. Блок «только сотрудникам» сервер
			// отдаёт лишь сотруднику — помечаем, чтобы не решить, будто его видят клиенты.
			case 'showcase':
				return (
					<Fragment key={section.id}>
						{cfg.staff_only === true && <div className="ht__staff-tag">блок видят только сотрудники</div>}
						<ShowcaseBlock blockId={section.id} config={cfg} />
					</Fragment>
				)

			default:
				return null
		}
	}

	// Блок внутри вкладки. Лента пресетов здесь — на общем движке разделов /bots (HomeFeedBlock), а не
	// урезанной веткой `trend_feed` выше: та обслуживает старые блоки рамки и остаётся как была.
	const renderTabBlock = (block: HomeSection) => {
		switch (block.section_type) {
			case 'showcase':
				return <ShowcaseBlock blockId={block.id} config={block.config} />
			case 'trend_feed': {
				const pop = sections.find((s) => s.section_type === 'popular_category')
				const pool = Array.isArray(pop?.config?.cards) ? (pop.config.cards as BackendCard[]) : []
				return <HomeFeedBlock section={block} rawPool={pool} />
			}
			default:
				return null
		}
	}

	// Активный бренд — для ambient-aura, floating-shadow, hint-pill, и rim-glow
	// активного tile. Подтягивается из selectedModel через detectBrand. CSS-
	// vars (--brand-glow / --brand-glow-ring / --brand-glow-soft) объявлены в
	// _chat.scss под [data-active-brand="..."] — заюзают все потомки .main-area
	// через обычное CSS-наследование переменных. Пустая строка = нет активной
	// модели, fallback CSS-vars применяются (нейтральный muted glow).
	const activeBrand = selectedModel ? detectBrand(selectedModel.provider, selectedModel.id) : null
	const activeBrandId = activeBrand?.id ?? ''

	return (
		<div className="main-area" data-active-brand={activeBrandId}>
			{/* Atmosphere + orbs переехали в components/GlobalAmbient.tsx
			    (App-level mount). Здесь .main-area оставляет data-active-brand
			    для других brand-reactive UI элементов (bot-tile, send-btn,
			    welcome-intent-hint), но сами orbs/atmosphere уже не дублируются. */}

			{/* SVG def для folder-силуэта папки в bot-selector. Тот же
			    pattern что в AlbumsGrid (#album-folder-clip), но с
			    собственным ID — не зависит от подгрузки страницы /bots. */}
			<svg width="0" height="0" aria-hidden="true" style={{ position: 'absolute' }}>
				<defs>
					<clipPath id="bot-folder-clip" clipPathUnits="objectBoundingBox">
						{/* Folder shape с СИЛЬНО скруглёнными боками: 3 угла
						    body-папки (top-right, bottom-right, bottom-left)
						    радиусом 0.15 + tab-язычок 34% слева сверху. */}
						<path d="M 0 0.10 Q 0 0, 0.10 0 L 0.34 0 Q 0.41 0, 0.43 0.05 Q 0.46 0.08, 0.52 0.09 L 0.85 0.09 Q 1 0.09, 1 0.24 L 1 0.85 Q 1 1, 0.85 1 L 0.15 1 Q 0 1, 0 0.85 Z" />
					</clipPath>
				</defs>
			</svg>

			{(() => {
				// Страховка легаси-порядка данных: если в конструкторе
				// brand_selector идёт ПРЯМО ПЕРЕД chat_input (исторический
				// порядок в БД) — рендерим наоборот: на главной поле ввода
				// всегда НАД плитками моделей. Если пара в данных уже в
				// правильном порядке — no-op.
				// Блок, живущий только во вкладках (раздел tab:<слаг>), в общий поток не идёт — его рисует HomeTabs.
				const ordered = sections.filter(isFrameSection)
				for (let i = 0; i < ordered.length - 1; i++) {
					const cur = ordered[i]
					const nxt = ordered[i + 1]
					if (cur.section_type === 'brand_selector' && nxt.section_type === 'chat_input') {
						ordered[i] = nxt
						ordered[i + 1] = cur
					}
				}
				return ordered.map((s) => renderSection(s))
			})()}

			{/* Только на ПЕРВОЙ загрузке: при рефетче (смена юзера) уже
			    отрисованный контент подменять заглушкой нельзя. */}
			{sectionsLoading && sections.length === 0 && <FeedSkeleton />}

			<footer className="footer">
				<div className="footer-bottom">
					<div className="footer-logo">
						<img src="/icons/logo.svg" alt="" className="header-logo-icon" />
						<span>Umnik AI</span>
					</div>
					<div className="footer-links">
						<a href="/offer" className="footer-link">
							Публичная оферта
						</a>
						<a href="/privacy" className="footer-link">
							Политика конфиденциальности
						</a>
					</div>
				</div>
				<div className="footer-company">
					<span className="footer-company-name">ИП СЕМЁНОВ СЕРГЕЙ АЛЕКСАНДРОВИЧ</span>
					<span className="footer-company-address">ИНН: 930702005998 · ОГРНИП: 325080000020352</span>
                                    <span className="footer-company-number">ЮЖНЫЙ Ф-Л ПАО «Банк ПСБ», г. Волгоград · БИК 041806715</span>
				</div>
			</footer>
		</div>
	)
}
